import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ExclamationCircleIcon } from '@heroicons/react/outline'

import { Button } from '../../../../components'
import { updateAllShippingCoveragesByMethod, bulkUpdateShippingCoveragesByIds } from '../../../../services/shippingMethodsServices'
import { FieldLabel } from './formPrimitives'
import { CoverageJobProgressPanel } from '../CoverageJobProgressPanel'
import { useMethodZones, ZoneSelect, ExtendedZoneSelect, NoneAppliesWarning, coverageUpdateError, UpdateErrorBanner } from './ZoneUpdateFields'

const M = 'lastmile_management.coverage_manager.update_modal'

// Above this many hours, warn the operator to double-check the SLA value.
const SLA_WARN_HOURS = 72

const INPUT_CLS = 'block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'

const onlyDigits = s => s.replace(/[^0-9]/g, '')

const Helper = ({ children }) => <div className="mt-1 text-xs text-gray-400">{children}</div>

const HoursInput = ({ value, onChange, placeholder, suffix }) => (
  <div className="relative">
    <input
      type="text"
      inputMode="numeric"
      value={value}
      onChange={e => onChange(onlyDigits(e.target.value))}
      placeholder={placeholder}
      className={`${INPUT_CLS} pr-14 placeholder-gray-400`}
    />
    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 sm:text-sm">{suffix}</span>
  </div>
)

/**
 * Applies SLA, SLA offset, status, zone and the extended-zone surcharge to a set of
 * coverages.
 *   - default: every coverage of the method (update-by-shipping-method).
 *   - when `coverageIds` is provided: only those coverages (bulk-update-by-ids).
 * Every field is optional and defaults to "keep": the payload carries only what the
 * operator actually set, so an SLA edit leaves the zone alone and vice versa. Returns a
 * job the shared progress view polls.
 *
 * Zone is offered ONLY on a selection. Method-wide, the backend refuses it
 * (ZONE_UPDATE_NOT_ALLOWED_METHOD_WIDE): one call would collapse every coverage of the
 * method onto a single zone with no history to revert it. The surcharge flag has no such
 * restriction and is offered in both modes.
 */
export const UpdateCoverageForm = ({ shippingMethod, coverageIds, onCancel, onClose, onUpdated }) => {
  const { t } = useTranslation()

  const targetsSelection = Array.isArray(coverageIds) && coverageIds.length > 0

  const [sla, setSla] = useState('')
  const [slaOffset, setSlaOffset] = useState('')
  const [status, setStatus] = useState('keep') // keep | active | paused
  const [zone, setZone] = useState('') // "" = keep each coverage's current zone
  const [extendedZone, setExtendedZone] = useState('') // "" | "true" | "false"
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [progressJobId, setProgressJobId] = useState(null)

  // Method-wide has no zone control, and the zone list is not cheap to build (it walks
  // every coverage row of the method), so it is fetched only when it can be used.
  const { zones, isLoading: zonesLoading, error: zonesError } = useMethodZones(targetsSelection ? shippingMethod?.id : null)

  // Nothing changes by default ("Mantener actual" + empty fields): the button
  // stays disabled until the operator actually picks a status or sets SLA/offset.
  // SLA and offset stay optional, so deactivating without touching them works.
  const hasChange = status !== 'keep' || sla.trim() !== '' || slaOffset.trim() !== '' || (targetsSelection && zone !== '') || extendedZone !== ''
  const canSubmit = hasChange && !submitting
  const slaTooHigh = parseInt(sla, 10) > SLA_WARN_HOURS || parseInt(slaOffset, 10) > SLA_WARN_HOURS

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const updateData = {}
      if (status !== 'keep') updateData.enabled = status === 'active'
      if (sla.trim() !== '') updateData.delivery_sla_in_hours = parseInt(sla, 10)
      if (slaOffset.trim() !== '') updateData.delivery_sla_offset = parseInt(slaOffset, 10)
      // Sent only when chosen. Passing the zone the coverages are already on would
      // re-run ZoneUpdateValidator against it, so a misconfigured current zone would
      // start refusing unrelated SLA edits.
      if (targetsSelection && zone !== '') updateData.zone = zone
      if (extendedZone !== '') updateData.extended_zone = extendedZone === 'true'
      const res = targetsSelection
        ? await bulkUpdateShippingCoveragesByIds(shippingMethod.id, coverageIds, updateData)
        : await updateAllShippingCoveragesByMethod(shippingMethod.id, updateData)
      if (res?.job_id) {
        setProgressJobId(res.job_id)
      } else {
        onUpdated?.()
        onClose?.()
      }
    } catch (e) {
      setError(coverageUpdateError(t, e))
    } finally {
      setSubmitting(false)
    }
  }

  // Job running / finished → show progress inline (replacing the form), so we
  // don't stack a second dialog on top of this modal.
  if (progressJobId) {
    return (
      <CoverageJobProgressPanel
        jobId={progressJobId}
        onClose={finalStatus => {
          setProgressJobId(null)
          if (finalStatus && finalStatus !== 'failed') {
            onUpdated?.()
            onClose?.()
          }
        }}
        onCompleted={() => onUpdated?.()}
      />
    )
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <FieldLabel>{t(`${M}.fields.sla`)}</FieldLabel>
          <HoursInput value={sla} onChange={setSla} placeholder={t(`${M}.fields.sla_placeholder`)} suffix={t(`${M}.fields.hours_suffix`)} />
          <Helper>{t(`${M}.fields.sla_help`)}</Helper>
        </div>

        <div>
          <FieldLabel>{t(`${M}.fields.sla_offset`)}</FieldLabel>
          <HoursInput value={slaOffset} onChange={setSlaOffset} placeholder={t(`${M}.fields.sla_offset_placeholder`)} suffix={t(`${M}.fields.hours_suffix`)} />
          <Helper>{t(`${M}.fields.sla_offset_help`)}</Helper>
        </div>

        <div>
          <FieldLabel>{t(`${M}.fields.status`)}</FieldLabel>
          <select value={status} onChange={e => setStatus(e.target.value)} className={INPUT_CLS}>
            <option value="keep">{t(`${M}.fields.status_keep`)}</option>
            <option value="active">{t(`${M}.fields.status_active`)}</option>
            <option value="paused">{t(`${M}.fields.status_paused`)}</option>
          </select>
          <Helper>{t(`${M}.fields.status_help`)}</Helper>
        </div>
      </div>

      <div className="border-t border-gray-100 pt-4">
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-500">{t(`${M}.zone_section_title`)}</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {targetsSelection ? (
            <ZoneSelect zones={zones} isLoading={zonesLoading} error={zonesError} value={zone} onChange={setZone} />
          ) : (
            <div className="text-xs leading-5 text-gray-500">{t(`${M}.zone_method_wide_note`)}</div>
          )}
          <ExtendedZoneSelect zones={zones} value={extendedZone} onChange={setExtendedZone} />
        </div>
      </div>

      <NoneAppliesWarning zone={targetsSelection ? zone : ''} />

      {/* Zone is withheld method-wide because one call would collapse every coverage
          onto a single zone with no per-CP history to revert it. The surcharge flag is
          the same shape of write — a method-wide `no` clears the ODA/OPA flag from every
          CP that carried it, and nothing records which ones those were — so it gets the
          argument said out loud rather than only the generic blast-radius banner. */}
      {!targetsSelection && extendedZone !== '' && (
        <div className="flex items-center gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 text-amber-500" />
          <span className="text-sm leading-5 text-amber-800">{t(`${M}.extended_zone_method_wide_warning`)}</span>
        </div>
      )}

      {slaTooHigh && (
        <div className="flex items-center gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 text-amber-500" />
          <span className="text-sm leading-5 text-amber-800">{t(`${M}.sla_warning`)}</span>
        </div>
      )}

      {hasChange && (
        <div className="flex items-center gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 text-amber-500" />
          <span className="text-sm leading-5 text-amber-800">
            {targetsSelection ? t(`${M}.selected.warning`, { count: coverageIds.length }) : t(`${M}.all.warning`)}
          </span>
        </div>
      )}

      <UpdateErrorBanner error={error} />

      <div className="flex gap-3 pt-1">
        <Button type="secondary" className="flex-1" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="primary" className="flex-1" disabled={!canSubmit} loading={submitting} loading_label={t(`${M}.submit`) + '…'} onClick={handleSubmit}>
          {t(`${M}.submit`)}
        </Button>
      </div>
    </div>
  )
}
